import React, { useRef, useState } from 'react';
import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/free-solid-svg-icons';
import { faCircleXmark } from '@fortawesome/free-regular-svg-icons';
import TeamPicker from './TeamPicker.jsx';
import TransferPanel from './TransferPanel.jsx';
import { activeMemberships, dateOnly, idOf, operationId, playerName, sortPlayers, today } from './transferModel.mjs';

const currentTeamsLabel = player => {
  const memberships = activeMemberships(player);
  if (!memberships.length) return player.teamsSeason?.length ? 'Aucune équipe active' : 'Équipe non renseignée';
  const names = memberships.map(membership => {
    const knownTeam = (player.teams || []).find(team => idOf(team) === idOf(membership.team));
    const name = [membership.name, membership.team?.name, knownTeam?.name]
      .find(value => typeof value === 'string' && value.trim());
    return name ? name.trim() : 'Équipe non renseignée';
  });
  return [...new Set(names)].join(', ');
};

const playerResultLabel = player => {
  const birthday = dateOnly(player.birthday);
  return [playerName(player), currentTeamsLabel(player), birthday.replace(/^(\d{4})-(\d{2})-(\d{2})$/, '$3/$2/$1')]
    .filter(Boolean).join(' — ');
};

export default function ArrivalEditor({ api, context, onAdd, onCancel, onDirty, existingIds, canCreate }) {
  const [mode, setMode] = useState('team');
  const [selection, setSelection] = useState({ competition: '', team: null });
  const [name, setName] = useState({ lastname: '', firstname: '' });
  const [results, setResults] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [source, setSource] = useState('');
  const [creating, setCreating] = useState(false);
  const [extra, setExtra] = useState({ birthday: '', nationality: '', positioning: '', number: '' });
  const [startDate, setStartDate] = useState(today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const version = useRef(0);
  const dirty = () => onDirty();
  const resetResults = () => { version.current++; setResults(null); setChosen(null); setSource(''); setError(''); dirty(); };
  const inContext = p => idOf(p.provider) === idOf(context.provider) && idOf(p.discipline) === idOf(context.discipline);
  const search = async () => {
    const request = ++version.current;
    setBusy(true); setError(''); setChosen(null);
    try {
      const filters = { provider: context.provider, discipline: context.discipline,
        ...(mode === 'team' ? { season: context.season, competition: selection.competition, teams: idOf(selection.team) } : name) };
      const r = await api.fetchPlayers(filters);
      if (request === version.current) setResults(sortPlayers((r.data || []).filter(inContext)));
    } catch { if (request === version.current) setError('La recherche a échoué. Réessayez.'); }
    finally { if (request === version.current) setBusy(false); }
  };
  const choose = async candidate => {
    setBusy(true); setError(''); setChosen(null);
    try {
    const p = await api.fetchPlayerDetails(candidate);
    setChosen(p); dirty();
    const memberships = activeMemberships(p, startDate);
    const matching = memberships.find(t => idOf(t.team) === idOf(selection.team));
    const clubs = memberships.filter(t => t.team && typeof t.team === 'object' && !t.team.isCountry);
    setSource(context.team.isCountry ? '' : idOf(matching?.team || (clubs.length === 1 ? clubs[0].team : '')));
    } catch (e) { setError(e.message || 'Impossible de charger la fiche du joueur.'); }
    finally { setBusy(false); }
  };
  const submit = async () => {
    setError(''); setBusy(true);
    try {
      let player = chosen;
      if (creating) {
        const normalized = { lastname: name.lastname.trim(), firstname: name.firstname.trim() };
        if (!normalized.lastname) throw new Error('Le nom est obligatoire.');
        const r = await api.checkExistingPlayer({ ...normalized, discipline: context.discipline, provider: context.provider });
        if (!Array.isArray(r.data)) throw new Error('Réponse de vérification des doublons inattendue.');
        if (r.data.length) throw new Error('Des joueurs similaires existent. Vérifiez-les dans la gestion des joueurs avant de créer une nouvelle fiche.');
        player = { ...normalized, ...Object.fromEntries(Object.entries(extra).filter(([,v]) => v)), provider: context.provider, discipline: context.discipline, teams: [], teamsSeason: [] };
      }
      if (!player) throw new Error('Sélectionnez un joueur.');
      const sourceTeam = source ? (player.teamsSeason || []).find(t => idOf(t.team) === source)?.team : null;
      const clubs = activeMemberships(player, startDate).filter(t => typeof t.team !== 'object' || !t.team.isCountry);
      if (!creating && !context.team.isCountry && clubs.length && !sourceTeam) throw new Error('Choisissez le club de départ.');
      onAdd({ key: operationId(), kind: 'arrival', isNew: creating, player, sourceTeam, destinationTeam: context.team, startDate });
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  };
  const arrivalName = creating ? playerName(name).trim() : chosen ? playerName(chosen) : '';
  return <TransferPanel className="transfers-editor transfers-arrival-editor"
    title={arrivalName || (creating ? 'Créer un joueur' : 'Ajouter un joueur')}
    toggleLabel={arrivalName ? `l’arrivée de ${arrivalName}` : 'la saisie d’une arrivée'}
    notice={error && <Alert severity="error">{error}</Alert>}
    actions={<Button className="transfers-button transfers-button--text" startIcon={<FontAwesomeIcon icon={faCircleXmark} />} disabled={busy} onClick={onCancel}>Annuler la saisie</Button>}>
    <Typography className="transfers-help">Indiquer le joueur qui vient d’arriver dans cette équipe.</Typography>
    {!creating && <>
      <TextField select fullWidth size="small" label="Rechercher" value={mode} disabled={busy}
        SelectProps={{ MenuProps: { className: 'transfers-menu' } }} onChange={e => { setMode(e.target.value); resetResults(); }}>
        <MenuItem value="team">Par équipe</MenuItem><MenuItem value="name">Par nom</MenuItem>
      </TextField>
      {mode === 'team' && <TeamPicker api={api} context={context} value={selection} disabled={busy}
        onChange={v => { setSelection(v); resetResults(); }} exclude={context.team} />}
    </>}
    {(mode === 'name' || creating) && <Box className="transfers-fields-row">
      {['lastname', 'firstname'].map(k => <TextField key={k} fullWidth size="small" label={k === 'lastname' ? 'Nom' : 'Prénom'} required={k === 'lastname'}
        value={name[k]} disabled={busy} onChange={e => { setName({ ...name, [k]: e.target.value }); resetResults(); }} />)}
    </Box>}
    {!creating && <>
      <Button className="transfers-button transfers-button--outline transfers-align-start" variant="outlined" onClick={search} disabled={busy || (mode === 'team' ? !selection.team : !name.lastname.trim())}>Rechercher</Button>
      {results?.length === 0 && <Alert severity="info">Aucun joueur trouvé.</Alert>}
      {!!results?.length && <TextField select fullWidth size="small" label="Joueur" value={idOf(chosen)} disabled={busy}
        helperText={chosen ? `ID : ${idOf(chosen)}` : undefined}
        SelectProps={{ MenuProps: { className: 'transfers-menu' }, renderValue: () => chosen ? playerResultLabel(chosen) : '' }}
        onChange={e => choose(results.find(p => idOf(p) === e.target.value))}>
        {results.map(p => <MenuItem key={idOf(p)} value={idOf(p)} title={`ID : ${idOf(p)}`} disabled={existingIds.includes(idOf(p)) || activeMemberships(p, startDate).some(t => idOf(t.team) === idOf(context.team))}>
          {playerResultLabel(p)}
        </MenuItem>)}
      </TextField>}
      {canCreate && results?.length === 0 && <Button className="transfers-button transfers-button--outline transfers-align-start" variant="outlined" startIcon={<FontAwesomeIcon icon={faUserPlus} />} disabled={busy} onClick={() => { setCreating(true); setChosen(null); setSource(''); dirty(); }}>Créer avec la saisie</Button>}
    </>}
    {creating && <>
      <Alert severity="info">Équipe ajoutée automatiquement : {context.team.name}</Alert>
      <Box className="transfers-fields-grid">
        {Object.keys(extra).map(k => <TextField key={k} fullWidth size="small" label={{ birthday: 'Date de naissance', nationality: 'Nationalité', positioning: 'Poste', number: 'Numéro' }[k]}
          type={k === 'birthday' ? 'date' : 'text'} InputLabelProps={{ shrink: true }} disabled={busy} value={extra[k]} onChange={e => { setExtra({ ...extra, [k]: e.target.value }); dirty(); }} />)}
      </Box>
    </>}
    {chosen && !creating && <TextField select fullWidth size="small" label="Équipe de départ" value={source} disabled={busy}
      SelectProps={{ MenuProps: { className: 'transfers-menu' } }} onChange={e => { setSource(e.target.value); dirty(); }}>
      <MenuItem value="">Sans départ (joueur libre ou sélection nationale)</MenuItem>
      {activeMemberships(chosen, startDate).map(t => <MenuItem key={t._id || idOf(t.team)} value={idOf(t.team)}>{t.team?.name || idOf(t.team)}</MenuItem>)}
    </TextField>}
    {(chosen || creating) && <>
      <TextField fullWidth size="small" type="date" label="Date d’arrivée" InputLabelProps={{ shrink: true }} value={startDate} disabled={busy} onChange={e => { setStartDate(e.target.value); dirty(); }} />
      <Button className="transfers-button transfers-button--primary" variant="contained" disableElevation disabled={busy} onClick={submit}>Ajouter au récapitulatif</Button>
    </>}
  </TransferPanel>;
}
